import { useSyncExternalStore } from 'react';
import { AddOn, PaginatedResponse } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';
import { loadAllPages } from './loadAllPages';

export interface AddOnListParams {
  page: number;
  pageSize: number;
  q?: string;
}

const buildQuery = (params: AddOnListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined
});

export const useAddOnsPage = (params: AddOnListParams): PaginatedResponse<AddOn> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('addOns', cb),
    () => 0,
    () => 0
  );
  return ctx.read('addOns', { kind: 'page', ...params }, (p: AddOnListParams) =>
    api.listStaffAddOns(buildQuery(p))
  );
};

export const useAddOnsAll = (): AddOn[] => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('addOns', cb),
    () => 0,
    () => 0
  );
  return ctx.read('addOns', { kind: 'all' }, () =>
    loadAllPages<AddOn>((p) => api.listStaffAddOns(p))
  );
};

export interface AddOnMutations {
  create: (addOn: AddOn) => Promise<void>;
  update: (addOn: AddOn) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useAddOnMutations = (): AddOnMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('addOns');
  return {
    create: async (addOn) => {
      await api.createAddOn(addOn);
      after();
    },
    update: async (addOn) => {
      await api.updateAddOn(addOn);
      after();
    },
    remove: async (id) => {
      await api.deleteAddOn(id);
      after();
    }
  };
};
